import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/StudentSlice";
import { RootState } from "src/redux/store";
import {
  useGetStudentsQuery,
  useChangeStudentStatusMutation,
} from "src/services/StudentServices";
import StudentListing from "./StudentListing";
import { Chip } from "@mui/material";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import UpdatePasswordDialogWrapper from "./UpdatePasswordDialog/UpdatePasswordDialogWrapper";
import moment from "moment";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { TbPasswordUser } from "react-icons/tb";
import { MdOutlinePublishedWithChanges } from "react-icons/md";
import { showToast } from "src/utils/showToaster";

type Props = {};

const StudentListingWrapper = (props: Props) => {
  const dispatch = useDispatch();

  const [openUpdatePasswordDialog, setOpenUpdatePasswordDialog] =
    useState(false);
  const [studentId, setStudentId] = useState<string>("");

  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    dateFilter,
    batches,
  } = useSelector((state: RootState) => state.student);

  const [changeStatus] = useChangeStudentStatusMutation();
  const { data, isLoading, isFetching } = useGetStudentsQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name", "mobile", "email"],
    page: page,
    filterBy: [
      {
        fieldName: "batches.batchId",
        value: batches ? batches?.map((el: any) => el?.value) : [],
      },
    ],
    dateFilter: {
      startDate: dateFilter?.start_date
        ? moment(dateFilter?.start_date).format("YYYY-MM-DD")
        : "",
      endDate: dateFilter?.end_date
        ? moment(dateFilter?.end_date).format("YYYY-MM-DD")
        : "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const getActionOptions = (studentId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 font-semibold">
            <TbPasswordUser className="text-lg" /> Update Password
          </div>
        ),
        onClick: () => {
          setOpenUpdatePasswordDialog(true);
          setStudentId(studentId);
        },
        actionName: UserModuleActionTypes.STUDENT_PASSWORD_CHANGE,
        moduleName: UserModuleNameTypes.STUDENT,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold ">
            <MdOutlinePublishedWithChanges className="text-lg" /> Change Status
          </div>
        ),

        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to change the status of student ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                changeStatus(studentId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.CHANGE_STATUS,
        moduleName: UserModuleNameTypes.STUDENT,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "min-w-[200px] max-w-[200px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-[12px] text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[11px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "name",
      extraClasses: "min-w-[200px] max-w-[200px]  ",
      headerName: "Name",
      flex: "flex-[1.5_1.5_0%] ",
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "min-w-[200px] max-w-[200px]",
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "min-w-[200px] max-w-[200px]",
    },
    {
      field: "branchId",
      headerName: "Branch Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => {
        return row?.branchId?.map((branch: any) => {
          return (
            <Chip
              label={branch?.branchName}
              key={branch?._id}
              className="my-2 me-2"
            />
          );
        });
      },
    },
    {
      field: "batches",
      extraClasses: "py-2 min-w-[350px] max-h-[120px] overflow-scroll",
      headerName: "Payment Details",
      flex: "flex-[1.5_1.5_0%] ",
      renderCell: (row: any) => {
        return (
          <div className="flex flex-col gap-2 ">
            {row?.batches?.map((el: any, ind: any) => {
              return (
                <div className="grid grid-cols-4 gap-4 p-2 bg-white border rounded-md shadow-sm">
                  <div className="flex flex-col ">
                    <span className="text-[12px] font-semibold text-primary-dark">
                      Batch
                    </span>
                    <span className="text-[11px]">{el?.name}</span>
                  </div>
                  <div className="flex flex-col ">
                    <span className="text-[12px] font-semibold text-primary-dark">
                      Due
                    </span>
                    <span className="text-[11px]">{el?.dueAmount} </span>
                  </div>
                  <div className="flex flex-col ">
                    <span className="text-[12px] font-semibold text-primary-dark">
                      Fees
                    </span>
                    <span className="text-[11px]">{el?.fees} </span>
                  </div>
                  <div className="flex flex-col ">
                    <span className="text-[12px] font-semibold text-primary-dark">
                      Next Installemnt
                    </span>
                    <span className="text-[11px]">
                      {el?.nextInstallmentDate}{" "}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },
    // {
    //   field: "password",
    //   headerName: "Update Password",
    //   flex: "flex-[1.5_1.5_0%]",
    //   extraClasses: "min-w-[250px]",
    //   renderCell: (row: any) => {
    //     return (
    //       <div>
    //         <ATMLoadingButton
    //           className="w-[70px] h-[30px]"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             setOpenUpdatePasswordDialog(true);
    //             setStudentId(row?._id);
    //           }}
    //         >
    //           Update
    //         </ATMLoadingButton>
    //       </div>
    //     );
    //   },
    // },
    {
      field: "isActive",
      headerName: "isActive",
      flex: "flex-[1_1_0%]",
      extraClasses: "py-2",
      align: "center",
      renderCell: (row: any) => {
        return (
          <div className="font-medium">
            {row?.isActive ? (
              <span className="text-green-600">Yes</span>
            ) : (
              <span className="text-red-600">No</span>
            )}{" "}
          </div>
        );
      },
    },

    {
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const CustomArray = [
    UserModuleActionTypes.CHANGE_STATUS,
    UserModuleActionTypes.STUDENT_PASSWORD_CHANGE,
  ];

  return (
    <div>
      <StudentListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.STUDENT,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
          actionArray: CustomArray,
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {openUpdatePasswordDialog ? (
        <UpdatePasswordDialogWrapper
          studentId={studentId}
          setStudentId={setStudentId}
          onClose={() => {
            setOpenUpdatePasswordDialog(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default StudentListingWrapper;
